/**
 * /2fa-setup — TOTP enrolment.
 *
 * Reached from two distinct entry points (both wired through this one
 * route, distinguished by `?required=`):
 *
 *  1. Optional — a user clicks "Set up two-factor auth" from the
 *     account menu while already signed-in. Skippable; the post-setup
 *     redirect is ?next= (defaults to /dashboard).
 *  2. M-1 mandatory gate — a super_admin/hr_manager identity tried to
 *     log in / activate / register and the backend 403'd
 *     `totp_enrollment_required`, leaving an enrolment-scoped
 *     identity-only session (no tenant, no refresh, no CSRF row). The
 *     login/activate caller bounced us here with `?required=1`. UI has
 *     no skip affordance in this mode.
 *
 * After verify succeeds we re-hydrate /me. If user_id is still 0 (the
 * M-1 enrolment-only session) we route to /select-workspace so the
 * now-2FA identity can pick a tenant; otherwise we honour ?next=.
 *
 * Migration note: 1:1 port of
 * `frontend/src/app/(auth)/2fa-setup/page.tsx`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Copy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi, toApiError } from "@/lib/api";
import { useAuth, safeNext } from "@/lib/auth";
import { totpCodeSchema, type TotpCodeValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";
import { TotpQr } from "@/components/auth/totp-qr";
import type { TOTPSetupResponse } from "@/types/auth";

const twoFaSetupSearchSchema = z.object({
  next: z.string().optional(),
  // Coerced to a number so the URL form ?required=1 stays a single
  // numeric literal. Falsy → optional/skippable flow.
  required: z.coerce.number().optional(),
});

export const Route = createFileRoute("/_auth/2fa-setup")({
  validateSearch: twoFaSetupSearchSchema,
  component: TwoFactorSetupPage,
});

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success(`${label} copied`);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Couldn't copy — select and copy manually");
        }
      }}
    >
      {copied ? (
        <Check className="h-4 w-4" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
      Copy
    </Button>
  );
}

function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const { next: nextRaw, required } = Route.useSearch();
  const { refresh } = useAuth();
  const [enrol, setEnrol] = useState<TOTPSetupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Guard the post-setup redirect against open-redirect.
  const nextPath = safeNext(nextRaw);
  const isMandatory = required === 1;

  const form = useForm<TotpCodeValues>({
    resolver: zodResolver(totpCodeSchema),
    defaultValues: { code: "" },
  });

  // Guards against React 19 StrictMode invoking the effect twice: a
  // second /totp/setup would stage a fresh secret server-side and
  // silently invalidate the backup codes the user just saved.
  const enrolStarted = useRef(false);

  const startEnrolment = useCallback(async () => {
    try {
      const res = await authApi.totpSetup();
      setEnrol(res);
    } catch (err) {
      const apiErr = toApiError(err);
      if (apiErr.error === "already_enabled") {
        toast.info("Two-factor authentication is already enabled.");
        void navigate({ to: nextPath, replace: true });
        return;
      }
      setSetupError(apiErr.message);
    } finally {
      setLoading(false);
    }
  }, [navigate, nextPath]);

  const retryEnrolment = useCallback(() => {
    setEnrol(null);
    setSetupError(null);
    setLoading(true);
    void startEnrolment();
  }, [startEnrolment]);

  useEffect(() => {
    if (enrolStarted.current) return;
    enrolStarted.current = true;
    void startEnrolment();
  }, [startEnrolment]);

  async function onSubmit(values: TotpCodeValues) {
    setSubmitting(true);
    try {
      await authApi.totpVerify({ code: values.code });
      const me = await refresh();
      toast.success("Two-factor authentication enabled.");
      // M-1 path: enrolment was forced on an IDENTITY-ONLY session
      // (user_id===0 / tenant_id===0). Going straight to /dashboard
      // would just bounce through the guard to the picker. Route to
      // /select-workspace explicitly so the now-2FA identity picks a
      // workspace and activates.
      if (me?.user_id === 0) {
        void navigate({
          to: "/select-workspace",
          search: { next: nextPath },
          replace: true,
        });
        return;
      }
      void navigate({ to: nextPath, replace: true });
    } catch (err) {
      handleAuthError(err, form.setError, ["code"]);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="mb-1 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <CardTitle className="text-2xl">
            {isMandatory
              ? "Two-factor auth is required"
              : "Set up two-factor auth"}
          </CardTitle>
        </div>
        <CardDescription>
          {isMandatory ? (
            <>
              Your role requires two-factor authentication before you
              can open any workspace. Set it up now with an
              authenticator app (Google Authenticator, Authy,
              1Password…) — this protects your whole account, every
              company you belong to. You can&apos;t skip this step.
            </>
          ) : (
            <>
              Add an extra layer of security with an authenticator app
              (Google Authenticator, Authy, 1Password…). This applies
              to your whole account — every company you belong to.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {!loading && setupError && (
          <div className="space-y-3">
            <p className="text-sm text-destructive">{setupError}</p>
            <Button
              type="button"
              variant="outline"
              onClick={retryEnrolment}
            >
              Try again
            </Button>
          </div>
        )}

        {!loading && enrol && (
          <>
            <div className="space-y-3">
              <p className="text-sm font-medium">
                1. Scan this QR code with your authenticator
              </p>
              <p className="text-xs text-muted-foreground">
                The code is rendered on this device — your secret is
                never sent to any third-party service.
              </p>
              <TotpQr otpauthUrl={enrol.otpauth_url} />

              <details className="rounded-md border border-input bg-secondary/60 p-3">
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                  Can&apos;t scan? Enter the secret manually
                </summary>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 rounded-md border border-input bg-secondary p-3">
                    <code className="flex-1 break-all text-sm">
                      {enrol.secret}
                    </code>
                    <CopyButton value={enrol.secret} label="Secret" />
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-input bg-secondary p-3">
                    <code className="flex-1 break-all text-xs text-muted-foreground">
                      {enrol.otpauth_url}
                    </code>
                    <CopyButton
                      value={enrol.otpauth_url}
                      label="Setup URI"
                    />
                  </div>
                </div>
              </details>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">
                2. Save your backup codes
              </p>
              <p className="text-xs text-muted-foreground">
                Each code works once if you lose your authenticator.
                Store them somewhere safe — they won&apos;t be shown
                again.
              </p>
              <div className="grid grid-cols-2 gap-2 rounded-md border border-input bg-secondary p-3">
                {enrol.backup_codes.map((c) => (
                  <code
                    key={c}
                    className="text-center text-sm tracking-wide"
                  >
                    {c}
                  </code>
                ))}
              </div>
              <CopyButton
                value={enrol.backup_codes.join("\n")}
                label="Backup codes"
              />
            </div>

            <div className="space-y-2 border-t border-border pt-4">
              <p className="text-sm font-medium">
                3. Confirm with a 6-digit code
              </p>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                  noValidate
                >
                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="sr-only">
                          Authenticator code
                        </FormLabel>
                        <FormControl>
                          <Input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            maxLength={6}
                            className="text-center text-lg tracking-[0.5em]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting}
                  >
                    {submitting && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    Enable two-factor auth
                  </Button>
                </form>
              </Form>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
